/**
 * Enhanced facets drawer behavior for Shopify
 * 
 * Features:
 * 1. Apply filter changes immediately when clicking inputs (but keep drawer open)
 * 2. Keep all applied filters when clicking Apply button
 * 3. Discard ALL filter changes when clicking outside the drawer
 * 4. Keep the drawer open while making selections
 * 5. Update available filter options based on current selection
 * 6. Hide color swatches with count of 0 (unavailable) only after filtering
 */

(function() {
  // Store the original form state and URL when the drawer opens
  let originalFormState = null;
  let originalUrl = null;
  let applyButtonClicked = false;
  let filteringApplied = false; // Track if filtering has been applied
  
  // Intercept drawer opening to capture original filter state
  document.addEventListener('click', function(event) {
    // Check if this is the filter button that opens the drawer
    const filterButton = event.target.closest('button[aria-controls="facets-drawer"]');
    if (!filterButton) return;
    
    // Find the target drawer that will be opened
    const drawerId = filterButton.getAttribute('aria-controls');
    if (!drawerId) return;
    
    const drawer = document.getElementById(drawerId);
    if (!drawer) return;
    
    // After the drawer is shown, capture the original form state and URL
    drawer.addEventListener('dialog:show', function onceHandler() {
      const form = drawer.querySelector('facets-form form');
      if (form) {
        // Store the original form data and current URL
        originalFormState = new FormData(form);
        originalUrl = window.location.href;
      }
      
      // Remove this one-time listener
      drawer.removeEventListener('dialog:show', onceHandler);
    }, { once: true });
  });
  
  // Handle clicks on the Apply button
  document.addEventListener('click', function(event) {
    // Find the apply button specifically
    const applyButton = event.target.closest('dialog-close-button');
    if (!applyButton) return;
    
    // Make sure it's in a facets-drawer
    const drawer = applyButton.closest('facets-drawer');
    if (!drawer) return;
    
    // Make sure it's not the explicit close button
    if (applyButton.hasAttribute('data-action') && applyButton.getAttribute('data-action') === 'close') {
      return;
    }
    
    // Mark that the Apply button was clicked - we just need to close the drawer
    // and keep the already-applied filters
    applyButtonClicked = true;
    
    // Reset stored original state since we're keeping the changes
    originalFormState = null;
    originalUrl = null;
  });
  
  // Revert filter changes when clicking outside the drawer
  document.addEventListener('click', function(event) {
    // If this is an Apply button click, don't do anything else
    if (applyButtonClicked) {
      applyButtonClicked = false;
      return;
    }
    
    // Find if the click is on the drawer background (to close it)
    const dialogClose = event.target.closest('.drawer__overlay, dialog-close-button[data-action="close"]');
    if (!dialogClose) return;
    
    // Find the drawer being closed
    const drawer = dialogClose.closest('.drawer') || document.querySelector('.drawer[open]');
    if (!drawer || !drawer.matches('facets-drawer')) return;
    
    // If we have an original URL, we need to revert to it (discarding all changes)
    if (originalUrl) {
      // Navigate back to the original state
      window.location.href = originalUrl;
      
      // Prevent the default closing behavior since we're navigating
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Reset the stored original state
    originalFormState = null;
    originalUrl = null;
  });
  
  // Override the default behavior by replacing elements
  document.addEventListener('DOMContentLoaded', function() {
    // Find all facets-drawer elements
    const drawers = document.querySelectorAll('facets-drawer');
    drawers.forEach(drawer => {
      // Clone the drawer to remove built-in event listeners
      const newDrawer = drawer.cloneNode(true);
      if (drawer.parentNode) {
        drawer.parentNode.replaceChild(newDrawer, drawer);
      }
    });
    
    // Do NOT hide color swatches on initial load
    // We'll only hide them after filtering
  });
  
  // Helper function to preserve checked state when updating filter options
  function preserveCheckedState(oldForm, newForm) {
    // Find all checkboxes and radios in the old form
    const oldInputs = oldForm.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked');
    
    // For each checked input in the old form, find the corresponding input in the new form and check it
    oldInputs.forEach(oldInput => {
      const name = oldInput.name;
      const value = oldInput.value;
      
      // Find the corresponding input in the new form
      const newInput = newForm.querySelector(`input[name="${name}"][value="${value}"]`);
      if (newInput) {
        newInput.checked = true;
      }
    });
    
    // Also preserve text/number inputs and selects
    const oldTextInputs = oldForm.querySelectorAll('input[type="text"], input[type="number"], select');
    oldTextInputs.forEach(oldInput => {
      const name = oldInput.name;
      const value = oldInput.value;
      
      if (value) {
        const newInput = newForm.querySelector(`[name="${name}"]`);
        if (newInput) {
          newInput.value = value;
        }
      }
    });
  }

  // Function to hide color swatches that have a count of 0 or unknown
  function hideUnavailableColorSwatches(swatchCounts = null) {
    // Only hide swatches if filtering has been applied
    if (!filteringApplied && !swatchCounts) {
      return; // Do nothing if filtering hasn't been applied yet
    }
    
    // Find all facets forms (both in drawer and sidebar)
    const facetsForms = document.querySelectorAll('facets-form');
    
    facetsForms.forEach(form => {
      // Find all color swatch inputs within this form
      const colorInputs = form.querySelectorAll('input[id*="filter."]');
      
      colorInputs.forEach(input => {
        // Find the corresponding label which shows the count information
        const colorSwatch = form.querySelector(`label[for="${input.id}"]`);
        if (!colorSwatch || !colorSwatch.classList.contains('color-swatch')) return;
        
        // Default to showing the swatch (changed default behavior)
        let count = 1; // Default to a positive count (show)
        let shouldShow = true; // Default to showing
        
        // If we have explicit count information from the latest filter update, use that
        if (swatchCounts && swatchCounts.has(input.id)) {
          count = swatchCounts.get(input.id);
          // Only hide if count is 0
          shouldShow = count > 0;
        } else if (filteringApplied) {
          // Try to extract count from the existing elements, but only if filtering has been applied
          
          // Check if this is a color option that's already selected - always show these
          if (input.checked) {
            shouldShow = true;
          } else {
            // First check if there's a parent with count information
            const parentWithCount = colorSwatch.closest('div');
            if (parentWithCount) {
              // Look for count information in the label text
              const labels = parentWithCount.querySelectorAll('label');
              labels.forEach(label => {
                const labelText = label.textContent.trim();
                if (labelText.includes('(') && labelText.includes(')')) {
                  const matches = labelText.match(/\((\d+)\)/);
                  if (matches && matches[1]) {
                    // We found a count, extract the number
                    if (label.getAttribute('for') === input.id) {
                      count = parseInt(matches[1], 10);
                      // Only hide if count is 0
                      shouldShow = count > 0;
                    }
                  }
                }
              });
            }
            
            // Check data attributes for count information if not found in text
            if (shouldShow && colorSwatch.dataset.count !== undefined) {
              count = parseInt(colorSwatch.dataset.count, 10);
              // Only hide if count is 0
              shouldShow = count > 0;
            }
            
            // If still not determined, try to look for count in sibling elements
            if (shouldShow) {
              const colorParent = colorSwatch.parentNode;
              if (colorParent) {
                const siblingText = colorParent.textContent.trim();
                const countMatch = siblingText.match(/\((\d+)\)/);
                if (countMatch && countMatch[1]) {
                  count = parseInt(countMatch[1], 10);
                  // Only hide if count is 0
                  shouldShow = count > 0;
                }
              }
            }
          }
        }
        
        // Store the count as a data attribute for future reference
        colorSwatch.dataset.count = count;
        
        // Show or hide based on availability
        if (shouldShow) {
          // Make sure it's visible
          colorSwatch.style.display = '';
          input.style.display = '';
        } else {
          // Hide the color swatch
          colorSwatch.style.display = 'none';
          input.style.display = 'none';
        }
      });
    });
  }

  // Function to extract count information from color swatch
  function extractColorSwatchCount(colorSwatch, inputId) {
    if (!colorSwatch) return 0; // Default to hiding if no swatch found
    
    // Check if this is a selected input - always consider these available
    const input = document.getElementById(inputId);
    if (input && input.checked) {
      return 1; // Return positive count for selected swatches
    }
    
    let count = 1; // Default to showing (positive count) - changed default behavior
    
    // Check for count in the parent's labels
    const parentWithCount = colorSwatch.closest('div');
    if (parentWithCount) {
      const allLabels = parentWithCount.querySelectorAll('label');
      for (const label of allLabels) {
        const labelText = label.textContent.trim();
        if (labelText.includes('(') && labelText.includes(')')) {
          const matches = labelText.match(/\((\d+)\)/);
          if (matches && matches[1] && label.getAttribute('for') === inputId) {
            count = parseInt(matches[1], 10);
            break;
          }
        }
      }
    }
    
    // If no count found through labels, use the data attribute if available
    if (count === 1 && colorSwatch.dataset.count !== undefined) {
      count = parseInt(colorSwatch.dataset.count, 10);
    }
    
    // If we still don't have a count, check if there are any siblings with count info
    if (count === 1) {
      const colorParent = colorSwatch.parentNode;
      if (colorParent) {
        const siblingText = colorParent.textContent.trim();
        const countMatch = siblingText.match(/\((\d+)\)/);
        if (countMatch && countMatch[1]) {
          count = parseInt(countMatch[1], 10);
        }
      }
    }
    
    return count;
  }

  // Function to apply filter changes via AJAX
  function applyFilters(form) {
    // Create a clone of the form to avoid interference with normal submission
    const formClone = form.cloneNode(true);
    document.body.appendChild(formClone);
    
    // Make the form invisible
    formClone.style.display = 'none';
    
    // Update form values from the original form
    const formData = new FormData(form);
    
    // Submit the clone to apply filters but prevent page unload
    formClone.addEventListener('submit', function(e) {
      // We want to apply the filters without closing the drawer
      // so we use AJAX to load the filtered results

      const facetsDrawer = form.closest('facets-drawer');
      if (facetsDrawer) {
        // If in drawer, we want to load the filtered results in the background
        e.preventDefault();
        
        // Submit the filters via AJAX and update the page without navigating away
        const url = new URL(formClone.action);
        url.search = new URLSearchParams(formData).toString();
        
        // Use the Fetch API to load the filtered results
        fetch(url.toString(), {
          headers: {
            'Accept': 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
        .then(response => response.text())
        .then(html => {
          // Now we can mark that filtering has been applied - and we should start hiding swatches
          filteringApplied = true;
          
          // Parse the response HTML
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Find the content we need to update - typically the product grid
          const newContent = doc.querySelector('.collection__main');
          if (newContent) {
            const currentContent = document.querySelector('.collection__main');
            if (currentContent) {
              // Replace the main content with the new filtered content
              currentContent.innerHTML = newContent.innerHTML;
            }
          }
          
          // Update the product count in the collection toolbar
          const newProductCount = doc.querySelector('.collection-toolbar__products-count');
          const currentProductCount = document.querySelector('.collection-toolbar__products-count');
          if (newProductCount && currentProductCount) {
            currentProductCount.innerHTML = newProductCount.innerHTML;
          }
          
          // Update the filter options in the drawer to match available options
          const newDrawerFacets = doc.querySelector('facets-drawer facets-form');
          const newSidebarFacets = doc.querySelector('.facets-sidebar facets-form');
          
          if (facetsDrawer && newDrawerFacets) {
            const currentFacetsForm = facetsDrawer.querySelector('facets-form');
            
            // Store references to expanded details elements
            const expandedDetails = [];
            const detailsElements = currentFacetsForm.querySelectorAll('details');
            detailsElements.forEach(details => {
              if (details.hasAttribute('open')) {
                expandedDetails.push(details.querySelector('summary').textContent.trim());
              }
            });
            
            // Store the checked state of both regular inputs and color swatches
            const checkedInputs = new Map();
            currentFacetsForm.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').forEach(input => {
              checkedInputs.set(`${input.name}-${input.value}`, true);
            });
            
            // Extract color swatch count information from the new results
            const swatchCounts = new Map();
            newDrawerFacets.querySelectorAll('input[id*="filter."]').forEach(input => {
              const colorSwatch = newDrawerFacets.querySelector(`label[for="${input.id}"]`);
              if (colorSwatch && colorSwatch.classList.contains('color-swatch')) {
                // Use our improved count extraction function
                const count = extractColorSwatchCount(colorSwatch, input.id);
                swatchCounts.set(input.id, count);
              }
            });
            
            // Now we need to properly update the content including color swatches
            // Instead of just setting innerHTML, we'll do a more careful replacement
            // that preserves event listeners on critical elements
            
            // Step 1: Replace all non-swatch filter groups first
            const oldFilterGroups = currentFacetsForm.querySelectorAll('details');
            const newFilterGroups = newDrawerFacets.querySelectorAll('details');
            
            oldFilterGroups.forEach((oldGroup, index) => {
              if (index < newFilterGroups.length) {
                const newGroup = newFilterGroups[index];
                const oldSummary = oldGroup.querySelector('summary');
                const groupName = oldSummary ? oldSummary.textContent.trim() : '';
                
                // Check if this is a color filter group (contains color swatches)
                const hasColorSwatches = oldGroup.querySelectorAll('.color-swatch').length > 0;
                
                // If not a color filter group, we can replace it completely
                if (!hasColorSwatches) {
                  // Replace the content of this group
                  oldGroup.innerHTML = newGroup.innerHTML;
                  
                  // Expand it if it was expanded before
                  if (expandedDetails.includes(groupName)) {
                    oldGroup.setAttribute('open', '');
                  }
                } else {
                  // For color filter groups, we need more careful replacement
                  // Keep the summary (header) the same
                  const oldContent = oldGroup.querySelector('.v-stack');
                  const newContent = newGroup.querySelector('.v-stack');
                  
                  if (oldContent && newContent) {
                    // Replace the content while preserving the structure
                    oldContent.innerHTML = newContent.innerHTML;
                    
                    // Make sure the group is expanded if it was before
                    if (expandedDetails.includes(groupName)) {
                      oldGroup.setAttribute('open', '');
                    }
                  }
                }
              }
            });
            
            // Now restore checked state for all inputs, including those connected to color swatches
            checkedInputs.forEach((isChecked, key) => {
              const [name, value] = key.split('-');
              const input = currentFacetsForm.querySelector(`input[name="${name}"][value="${value}"]`);
              if (input) {
                input.checked = true;
              }
            });
            
            // After updating the DOM, hide unavailable color swatches using the count information
            // But only because filtering has been applied
            hideUnavailableColorSwatches(swatchCounts);
          }
          
          // Also update the sidebar facets if they exist
          const currentSidebarFacets = document.querySelector('.facets-sidebar facets-form');
          if (currentSidebarFacets && newSidebarFacets) {
            // Store the checked state
            const checkedInputs = new Map();
            currentSidebarFacets.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').forEach(input => {
              checkedInputs.set(`${input.name}-${input.value}`, true);
            });
            
            // Extract color swatch count information from the sidebar facets
            const sidebarSwatchCounts = new Map();
            newSidebarFacets.querySelectorAll('input[id*="filter."]').forEach(input => {
              const colorSwatch = newSidebarFacets.querySelector(`label[for="${input.id}"]`);
              if (colorSwatch && colorSwatch.classList.contains('color-swatch')) {
                // Use our improved count extraction function
                const count = extractColorSwatchCount(colorSwatch, input.id);
                sidebarSwatchCounts.set(input.id, count);
              }
            });
            
            // Replace the content
            currentSidebarFacets.innerHTML = newSidebarFacets.innerHTML;
            
            // Restore checked state
            checkedInputs.forEach((isChecked, key) => {
              const [name, value] = key.split('-');
              const input = currentSidebarFacets.querySelector(`input[name="${name}"][value="${value}"]`);
              if (input) {
                input.checked = true;
              }
            });
            
            // Apply hiding to sidebar facets too
            hideUnavailableColorSwatches(sidebarSwatchCounts);
          }
          
          // Update the URL in the browser history, but don't navigate
          const newUrl = url.toString();
          window.history.replaceState({ path: newUrl }, '', newUrl);
          
          // Re-attach event listeners to color swatches
          attachColorSwatchListeners();
        })
        .catch(error => {
          console.error('Error applying filters:', error);
        });
      } else {
        // If in sidebar, just submit normally
        return true;
      }
    });
    
    // Submit the clone form to apply filters
    if (HTMLFormElement.prototype.requestSubmit) {
      formClone.requestSubmit();
    } else {
      formClone.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
    
    // Remove the clone form after submission
    setTimeout(() => {
      document.body.removeChild(formClone);
    }, 100);
  }
  
  // Function to add click listeners to all color swatches
  function attachColorSwatchListeners() {
    const colorSwatches = document.querySelectorAll('.color-swatch');
    
    colorSwatches.forEach(swatch => {
      // Skip if we've already processed this swatch (has data-processed attribute)
      if (swatch.hasAttribute('data-processed')) return;
      
      // Mark as processed to avoid duplicate event listeners
      swatch.setAttribute('data-processed', 'true');
      
      swatch.addEventListener('click', function(event) {
        event.preventDefault();
        
        // Find the associated input
        const inputId = swatch.getAttribute('for');
        if (!inputId) return;
        
        const input = document.getElementById(inputId);
        if (!input) return;
        
        const form = input.closest('facets-form form');
        if (!form) return;
        
        // Toggle the input state
        if (input.type === 'checkbox') {
          input.checked = !input.checked;
        } else if (input.type === 'radio') {
          input.checked = true;
        }
        
        // Trigger change event
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
  
  // Handle changes to apply filters immediately but keep drawer open
  document.addEventListener('change', function(event) {
    const input = event.target;
    if (!input.matches('input[type="checkbox"], input[type="radio"], input[type="text"], input[type="number"], select')) return;
    
    const form = input.closest('facets-form form');
    if (!form) return;
    
    // Apply filters via AJAX
    applyFilters(form);
  });

  // Handle clicks on color swatches to apply filters immediately but keep drawer open
  document.addEventListener('DOMContentLoaded', function() {
    // Add event listeners to all initial color swatches
    attachColorSwatchListeners();
  });
})(); 